import { Inject, Injectable } from '@angular/core';
import {
  PagedResponse,
  AutomationReplacementChipsDataService,
} from '../common/components/automation-replacement-chips/interfaces';
import { map, Observable, of, take } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Environment, EnvironmentService } from '@galaxy/core';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { switchMap } from 'rxjs/operators';

export interface ActionList {
  id: string;
  name: string;
}

interface PagedApiResponse {
  actionLists: ActionList[];
  nextPage: number;
  hasMore: boolean;
  totalActionListsCount: number;
}

@Injectable({ providedIn: 'root' })
class ActionListApiService {
  apiOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    withCredentials: true,
  };

  private host: string;
  private lookupUrl = `${this.actionListServicesHost}/_ajax/v1/action-lists/get/`;

  constructor(public http: HttpClient, private environmentService: EnvironmentService) {}

  private get actionListServicesHost(): string {
    if (this.host) {
      return this.host;
    }
    switch (this.environmentService.getEnvironment()) {
      case Environment.LOCAL:
        this.host = '';
        break;
      case Environment.DEMO:
        this.host = 'https://partner-central-demo.appspot.com';
        break;
      case Environment.PROD:
        this.host = 'https://partners.vendasta.com';
        break;
    }
    return this.host;
  }

  public lookupActionLists(
    namespace: string,
    page = 0,
    pageSize,
    searchString: string = null,
  ): Observable<PagedApiResponse> {
    const params = {
      partnerId: namespace,
      page: page,
      querySize: pageSize,
      marketIds: [],
    };

    const SEARCH_STRING_KEY = 'searchString';
    if (searchString) {
      params[SEARCH_STRING_KEY] = searchString;
    }

    return this.http.post(this.lookupUrl, params, this.apiOptions).pipe(
      map((resp: any) => ({
        actionLists: resp?.data?.actionLists,
        nextPage: resp?.data?.nextPage || 0,
        hasMore: resp?.data?.nextPage > 0,
        totalActionListsCount: resp?.data?.totalActionListsCount || 0,
      })),
    );
  }
}

export interface ActionListInterface {
  id: string;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class ActionListsSelectorService implements AutomationReplacementChipsDataService<string> {
  supportsSearching = true;

  constructor(
    private readonly actionListsService: ActionListApiService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {}

  doLookup(searchTerm: string, cursor: string, pageSize: number): Observable<PagedResponse<string>> {
    let page = Number(cursor);
    if (!page) {
      page = 0;
    }
    return this.namespace$.pipe(
      switchMap((namespace) => this.actionListsService.lookupActionLists(namespace, page, pageSize, searchTerm)),
      take(1),
      map((res) => {
        const results = res?.actionLists.map((a) => a.name);
        const nextCursor = res?.nextPage.toString();
        const hasMore = res?.hasMore;
        return { results, nextCursor, hasMore };
      }),
    );
  }

  convertDataToDisplay(s: string): Observable<string> {
    return of(s);
  }

  convertDataToFormValue(s: string): any {
    return s;
  }

  getMulti(x: string[]): Observable<string[]> {
    return of(x);
  }

  get(actionListName: string): Observable<ActionListInterface> {
    return this.namespace$.pipe(
      switchMap((namespace) => this.actionListsService.lookupActionLists(namespace, 0, 1, actionListName)),
      map((resp) => {
        if (resp?.actionLists?.length > 0) {
          return resp.actionLists[0];
        }
        return { id: '', name: '' };
      }),
    );
  }
}
